import React, { useState } from 'react'
import { withStyles } from '@material-ui/core'
import styles from './styles'
import { Button } from 'src/components/app-ui'
import { Icon } from '@iconify/react'
import {
  templateDataCollection,
  templateDataCourse,
  templateDataShowCase,
  withFreshCatalogIds,
} from '../../data'
import { patchCourseLandingPageAction } from 'src/reducers/course/action'
import {
  getWorkspaceLogo,
  pathPresetStyle,
  useWorkspaceBranding,
} from 'src/utils/workspace-logo'
import { patchCollectionLandingPageAction } from 'src/reducers/collection/action'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import {
  messageBoxHideAction,
  messageBoxShowAction,
} from 'src/reducers/message-box/action'
import MessageBoxContent, { MessageBoxType } from 'src/model/message-box'
import { ButtonActionType, ButtonData, ButtonType } from 'src/model/button'
import { headerPromoTemplate } from 'src/components/editor/components/custom-plugins/simple-box/header-promo-template'
import { headerDefaultTemplate } from 'src/components/editor/components/custom-plugins/simple-box/header-default-template'
import { headerHeroTemplate } from 'src/components/editor/components/custom-plugins/simple-box/header-hero-template'
import { editorEnum } from 'src/model/editor/editorEnum'
import { setPageAccent } from 'src/components/editor/components/custom-plugins/simple-box/catalog'
import { headerBackgroundCss } from 'src/utils/default-header-background'

const TemplateSelect = props => {
  const {
    classes,
    pageUrl,
    setHtmlContent,
    setSelectedTemplateId,
    setSelectedFont,
    setPreviewTemplate,
  } = props
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const { id } = useParams()
  const dispatch = useDispatch()
  const collection = useSelector(state => state.collection.collection) || ''
  let activeTemplateData

  if (pageUrl === 'mini-course') {
    activeTemplateData = templateDataCourse
  } else if (collection && collection.type === 1) {
    activeTemplateData = templateDataShowCase
  } else {
    activeTemplateData = templateDataCollection
  }

  // Presets save their own color and font. The Default preset's are the system
  // defaults, which count as "not chosen": pages show the workspace branding's
  // instead, live, so nothing from the branding is written to the page.
  const branding = useWorkspaceBranding()
  const isPath =
    pageUrl !== 'mini-course' && !(collection && collection.type === 1)
  const withBrandingStyle = template =>
    isPath && template
      ? {
          ...template,
          ...pathPresetStyle(template, branding || landingPageData),
        }
      : template
  const withPresetStyle = template => template

  const courseSettings = useSelector(state => state.course.landingPageSettings)
  const collectionSettings = useSelector(
    state => state.collection.landingPageSettings,
  )
  const collectionGeneralSettings = useSelector(
    state => state.collection.generalSettings,
  )
  const certificate = useSelector(state => state.certificate?.certificate)
  const paymentSetting = useSelector(state => state.certificate?.paymentSetting)
  const certificatePayment =
    pageUrl === 'mini-course'
      ? null
      : {
          isConfigured:
            !!certificate?.isActive &&
            !!paymentSetting?.isPaymentConfigured &&
            (paymentSetting?.amount ?? 0) > 0,
          amount: paymentSetting?.amount,
          currency: paymentSetting?.currency,
          priceType: paymentSetting?.priceType,
          priceMetadata: paymentSetting?.priceMetadata,
        }

  const landingPageData =
    pageUrl === 'mini-course' ? courseSettings || {} : collectionSettings || {}

  const course = useSelector(state => state.course.course)

  // Open by default until the landing page has been saved once.
  const shouldBeExpanded =
    landingPageData &&
    landingPageData.metadata !== null &&
    landingPageData.initialized !== true

  const [expanded, setExpanded] = useState(shouldBeExpanded)

  // Template'in verilerini kullanarak geçici landingPageData oluştur
  const createTempLandingPageData = template => {
    return {
      ...landingPageData,
      font: template.font,
      primaryColor: template.primaryColor,
      sections: template.value,
      logo: landingPageData.logo || getWorkspaceLogo(),
      localizations: landingPageData.localizations,
    }
  }
  const getTemplateHtml = template => {
    const tempLandingPageData = createTempLandingPageData(template)

    const headerSection = template.value?.find(
      x => x.type === editorEnum.headerType,
    )
    const headerType = headerSection?.content?.type || 1

    console.log('Getting template HTML:', {
      headerType,
      headerSection: headerSection?.content,
    })

    switch (headerType) {
      case 2:
        return headerHeroTemplate(
          tempLandingPageData,
          course,
          collection,
          pageUrl,
          collectionSettings,
          collectionGeneralSettings,
          certificatePayment,
        )
      case 3: // PROMO
        return headerPromoTemplate(
          tempLandingPageData,
          course,
          collection,
          pageUrl,
          collectionSettings,
          collectionGeneralSettings,
          certificatePayment,
        )
      case 1: // DEFAULT
      default:
        return headerDefaultTemplate(
          tempLandingPageData,
          course,
          collection,
          pageUrl,
          collectionSettings,
          collectionGeneralSettings,
        )
    }
  }

  const getBackgroundStyle = template => {
    const headerSection = template.value?.find(
      x => x.type === editorEnum.headerType,
    )
    const headerType = headerSection?.content?.type || 1

    if (headerType === 2) {
      const backgroundColor =
        headerSection?.content?.backgroundColor || '#FFFFFF'
      return { backgroundColor }
    } else {
      const imageUrl = headerSection?.content?.image

      if (imageUrl) {
        const processedUrl = imageUrl.startsWith('http')
          ? imageUrl
          : `https://cdn.minicoursegenerator.com/${imageUrl}`
        return {
          background: headerBackgroundCss(
            processedUrl,
            0.3,
            template.primaryColor,
          ),
        }
      }
    }
    return {}
  }

  const getAlignmentStyle = template => {
    const headerSection = template.value?.find(
      x => x.type === editorEnum.headerType,
    )
    const alignment = headerSection?.content?.alignment || 'center'

    return {
      textAlign: alignment,
      alignItems:
        alignment === 'center'
          ? 'center'
          : alignment === 'right'
          ? 'flex-end'
          : 'flex-start',
    }
  }

  const updateDomWithTemplate = template => {
    // The empty catalog's icon and the "Your learning" link read their colour from here. They
    // render after an async fetch, so they would miss the per-element recolouring below.
    setPageAccent(template.primaryColor)

    const editorHeaderContent = document.querySelector('.editor-header-content')
    const editorHeaderContainer = document.querySelector(
      '.editor-header-container',
    )

    console.log('Updating DOM with template:', {
      id: template.id,
      name: template.name,
      contentFound: !!editorHeaderContent,
      containerFound: !!editorHeaderContainer,
    })

    if (editorHeaderContent && editorHeaderContainer) {
      const headerSection = template.value?.find(
        x => x.type === editorEnum.headerType,
      )
      const templateHtml = getTemplateHtml(template)
      editorHeaderContent.innerHTML = templateHtml

      editorHeaderContainer.style.background = ''
      editorHeaderContainer.style.backgroundColor = ''
      editorHeaderContainer.style.backgroundImage = ''
      editorHeaderContainer.style.backgroundSize = ''
      editorHeaderContainer.style.backgroundPosition = ''
      editorHeaderContainer.style.backgroundRepeat = ''

      if (headerSection?.content?.type === 2) {
        const bgColor = headerSection?.content?.backgroundColor || '#FFFFFF'
        editorHeaderContainer.style.backgroundColor = bgColor
      } else {
        const backgroundStyle = getBackgroundStyle(template)

        if (backgroundStyle.background) {
          editorHeaderContainer.style.background = backgroundStyle.background
        } else if (backgroundStyle.backgroundColor) {
          editorHeaderContainer.style.backgroundColor =
            backgroundStyle.backgroundColor
        }
      }

      const alignmentStyle = getAlignmentStyle(template)
      Object.keys(alignmentStyle).forEach(key => {
        editorHeaderContent.style[key] = alignmentStyle[key]
      })

      setTimeout(() => {
        applyFontStyles(template)
      }, 50)
    } else {
      console.error('Could not find editor elements')
    }
  }

  const applyFontStyles = template => {
    const textElements = document.querySelectorAll(
      '.course-container-header-text, .hero-title, .promo-title, .hero-description, .promo-description, .promo-feature-text',
    )
    textElements.forEach(element => {
      element.style.setProperty('font-family', template.font, 'important')
    })

    const headerSection = template.value?.find(
      x => x.type === editorEnum.headerType,
    )
    const headerType = headerSection?.content?.type

    if (headerType === 3) {
      const textColor = headerSection?.content?.textColor || '#FFFFFF'
      const promoTexts = document.querySelectorAll(
        '.promo-title, .promo-description',
      )
      promoTexts.forEach(element => {
        element.style.setProperty('color', textColor, 'important')
      })
    }

    if (pageUrl === 'mini-course') {
      const startButtons = document.querySelectorAll(
        '.start-button-text-header, .start-button',
      )
      startButtons.forEach(button => {
        button.style.setProperty('font-family', template.font, 'important')
        if (button.classList.contains('start-button-text-header')) {
          button.style.setProperty('color', template.primaryColor, 'important')
        }
        if (button.classList.contains('start-button')) {
          button.style.setProperty(
            'background',
            template.primaryColor,
            'important',
          )
        }
      })
    }

    const heroButtons = document.querySelectorAll(
      '.hero-start-course-btn, .hero-see-more-btn',
    )
    heroButtons.forEach(button => {
      button.style.setProperty('font-family', template.font, 'important')
      if (button.classList.contains('hero-start-course-btn')) {
        button.style.setProperty(
          'background-color',
          template.primaryColor,
          'important',
        )
      }
      if (button.classList.contains('hero-see-more-btn')) {
        button.style.setProperty('color', template.primaryColor, 'important')
        button.style.setProperty(
          'border-color',
          template.primaryColor,
          'important',
        )
      }
    })

    const promoButtons = document.querySelectorAll('.promo-get-started-btn')
    promoButtons.forEach(button => {
      button.style.setProperty('font-family', template.font, 'important')
      button.style.setProperty(
        'background-color',
        template.primaryColor,
        'important',
      )
    })

    const priceTags = document.querySelectorAll(
      '.course-container-header-price',
    )
    priceTags.forEach(priceTag => {
      priceTag.style.setProperty('font-family', template.font, 'important')
      const svg = priceTag.querySelector('svg path')
      if (svg) {
        svg.setAttribute('fill', template.primaryColor)
      }
    })

    const checkIcons = document.querySelectorAll('.promo-check-icon')
    checkIcons.forEach(icon => {
      icon.style.backgroundColor = template.primaryColor
    })
  }

  const revertOriginal = () => {
    const landingPageMain = landingPageData?.sections?.find(
      x => x.type === editorEnum.mainType,
    )
    const htmlContent = landingPageMain?.content?.html

    const originalTemplate = {
      id: 0,
      font: landingPageData.font,
      primaryColor: landingPageData.primaryColor,
      value: landingPageData.sections,
    }

    updateDomWithTemplate(originalTemplate)
    if (setPreviewTemplate) setPreviewTemplate(null)

    if (htmlContent) {
      setHtmlContent(htmlContent)
    }
  }

  const handleTemplateClick = picked => {
    const template = withPresetStyle(picked)
    setSelectedTemplate(template.id)

    const headerSection = template.value?.find(
      x => x.type === editorEnum.headerType,
    )

    console.log('Template clicked:', {
      id: template.id,
      name: template.name,
      type: headerSection?.content?.type,
      font: template.font,
      headerContent: headerSection?.content,
    })

    if (setSelectedTemplateId) {
      setSelectedTemplateId(template.id)
      setSelectedFont(template.font)
    }
    // The header preview renders from this, so a later re-render (e.g. once the
    // path's certificate payment loads) keeps showing the picked preset.
    if (setPreviewTemplate) setPreviewTemplate(template)

    if (setHtmlContent && template.value) {
      const mainSection = template.value?.find(
        x => x.type === editorEnum.mainType,
      )
      const htmlContent = mainSection?.content?.html

      updateDomWithTemplate(template)

      if (htmlContent) {
        setHtmlContent(htmlContent)
      }
    }
  }
  const applyPreset = () => {
    if (landingPageData.initialized === true) {
      dispatch(
        messageBoxShowAction(
          MessageBoxContent({
            title: `<div style="display: flex; align-items: center; justify-content: center; gap: 8px; color: var(--mcg-dark, #002E47); font-family: Inter; font-size: 18px; font-weight: 600; line-height: 28px;">
            <Typography>Replace Current Settings?</Typography>
          </div>`,
            type: MessageBoxType.WARNING,
            message:
              'Changing the landing page theme will delete your current settings and texts. Continue anyway?',
            buttons: [
              new ButtonData(
                ButtonType.NORMAL,
                'Cancel',
                ButtonActionType.NO,
                () => {
                  dispatch(messageBoxHideAction())
                  revertOriginal()
                },
              ),
              new ButtonData(
                ButtonType.ACTION,
                'Continue',
                ButtonActionType.YES,
                () => {
                  handleApplyPreset()
                  dispatch(messageBoxHideAction())
                  // Sayfayı en üste scroll et
                  window.scrollTo({ top: 0, behavior: 'smooth' })
                },
              ),
            ],
            component: (
              <div
                style={{
                  gap: '8px',
                  color: 'var(--mcg-dark, #002E47)',
                  fontFamily: 'Inter',
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '22px',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <p className={classes.title}>
                  Changing the landing page theme will delete your current
                  settings and texts. Continue anyway?
                </p>
              </div>
            ),
            maxWidth: '500px',
          }),
        ),
      )
    } else {
      handleApplyPreset()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const handleApplyPreset = () => {
    if (selectedTemplate === null) {
      return
    }

    const patchAction =
      pageUrl === 'mini-course'
        ? patchCourseLandingPageAction
        : patchCollectionLandingPageAction

    const selectedTemplateObject = withPresetStyle(
      pageUrl === 'mini-course'
        ? templateDataCourse.find(template => template.id === selectedTemplate)
        : collection && collection.type === 1
        ? templateDataShowCase.find(
            template => template.id === selectedTemplate,
          )
        : templateDataCollection.find(
            template => template.id === selectedTemplate,
          ),
    )

    if (!selectedTemplateObject) return

    dispatch(
      patchAction(id, [
        {
          path: 'initialized',
          value: true,
        },
        {
          path: 'primaryColor',
          value: selectedTemplateObject.primaryColor,
        },
        {
          path: 'font',
          value: selectedTemplateObject.font,
        },
        {
          path: 'sections',
          value: withFreshCatalogIds(selectedTemplateObject.value),
        },
      ]),
    )

    setSelectedTemplate(null)
  }

  const closeApplyPreset = () => {
    const patchAction =
      pageUrl === 'mini-course'
        ? patchCourseLandingPageAction
        : patchCollectionLandingPageAction

    dispatch(
      patchAction(id, [
        {
          path: 'initialized',
          value: true,
        },
      ]),
    )
  }

  const handleStartBlank = () => {
    setSelectedTemplate(null)

    if (setSelectedTemplateId) {
      setSelectedTemplateId(null)
      setSelectedFont('Inter')
    }

    if (setHtmlContent) {
      setHtmlContent('')
    }
  }

  // Presets don't store which one was applied; match the saved font and
  // colour to name it ("Current").
  // Paths created while the branding was copied into them saved Default with
  // the branding style, so match that too.
  const currentTemplate = activeTemplateData.find(template =>
    [template, withBrandingStyle(template)].some(
      style =>
        landingPageData.initialized === true &&
        style.font === landingPageData.font &&
        style.primaryColor === landingPageData.primaryColor,
    ),
  )
  const selectedTemplateName = activeTemplateData.find(
    template => template.id === selectedTemplate,
  )?.name

  const handleApply = () =>
    landingPageData.initialized === true ? applyPreset() : handleApplyPreset()

  return (
    <div className={classes.presetCard}>
      <div className={classes.presetHead}>
        <button
          type="button"
          className={classes.presetToggle}
          onClick={() => setExpanded(open => !open)}
          aria-expanded={Boolean(expanded)}
        >
          <Icon
            icon="tabler:chevron-down"
            className={classes.presetChevron}
            style={{ transform: expanded ? 'none' : 'rotate(-90deg)' }}
          />
          Get started with a preset
          {currentTemplate && (
            <span className={classes.presetCurrent}>
              Current: {currentTemplate.name}
            </span>
          )}
        </button>
        {expanded && (
          <div className={classes.presetActions}>
            {selectedTemplate !== null && (
              <Button
                variant="ghost"
                size="small"
                onClick={() => {
                  revertOriginal()
                  setSelectedTemplate(null)
                }}
              >
                Revert original
              </Button>
            )}
            <Button
              size="small"
              disabled={selectedTemplate === null}
              onClick={handleApply}
            >
              {selectedTemplateName
                ? `Apply ${selectedTemplateName}`
                : 'Apply preset'}
            </Button>
          </div>
        )}
      </div>

      {expanded && (
        <div className={classes.presetRow}>
          {activeTemplateData.map(template => (
            <button
              key={template.id}
              type="button"
              className={classes.presetItem}
              style={{
                border:
                  selectedTemplate === template.id
                    ? '2px solid #015FFB'
                    : '1px solid #E5E7EB',
              }}
              onClick={() => handleTemplateClick(template)}
            >
              <span
                className={classes.presetThumb}
                style={{ backgroundImage: `url(${template.thumbnail})` }}
              >
                {currentTemplate?.id === template.id && (
                  <span className={classes.presetBadge}>CURRENT</span>
                )}
              </span>
              <span className={classes.presetLabel}>{template.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default withStyles(styles)(TemplateSelect)
